import axios from "axios";
import { trackEvent, trackException } from "./telemetry";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    __authTokenSnapshot?: string;
  }
}

const defaultApiBaseUrl =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:5000/api"
    : "/api";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || defaultApiBaseUrl,
});

const AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/change-password",
  "/auth/set-password",
  "/auth/forgot-password",
  "/auth/reset-password",
];

// Attach JWT from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  config.__authTokenSnapshot = token || "";
  if (token) {
    config.headers = config.headers ?? {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// On 401, clear stored credentials and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const statusCode = error.response?.status ? String(error.response.status) : "unknown";
    const requestUrl = error.config?.url || "unknown";
    trackEvent("frontend_api_error", {
      statusCode,
      method: (error.config?.method || "unknown").toUpperCase(),
      url: requestUrl,
    });
    trackException(error, {
      statusCode,
      url: requestUrl,
    });

    const requestPath = String(error.config?.url || "");
    const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => requestPath.includes(endpoint));
    const currentToken = localStorage.getItem("authToken") || "";
    const requestToken = error.config?.__authTokenSnapshot || "";
    const hasAuthToken = !!currentToken;
    const requestUsedCurrentToken = requestToken === currentToken;

    if (error.response?.status === 401 && !isAuthEndpoint && hasAuthToken && requestUsedCurrentToken) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ─── Types ───────────────────────────────────────────────────────────────────

export type UserRole = "cc_owner" | "cc_leader" | "bu_leader" | "vp" | "admin";

export interface CostCenterInfo {
  cost_center_number: string;
  cost_center_name: string;
  cost_center_owner: string;
  deactivated_from_fy?: string | null;
}

export interface AuthenticatedUser {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  owned_cost_center: string | null;
  cost_center_name?: string | null;
  cost_center_owner?: string | null;
  cost_centers?: CostCenterInfo[];
  is_first_login: boolean;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: AuthenticatedUser;
}

export interface ChangePasswordPayload {
  email: string;
  old_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export interface CostCenter {
  cost_center_number: string;
  cost_center_name: string;
  cost_center_owner: string;
  business_unit?: string;
}

export interface BusinessUnit {
  id: number;
  code: string;
  name: string;
  cc_count?: number;
  created_at?: string;
}

export interface UserRecord {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  manager_id: number | null;
  manager_name?: string;
  owned_ccs: string | null;       // comma-separated CC numbers from user_cost_centers
  assigned_bus: string | null;     // comma-separated "id:name" from user_business_units
  is_first_login: boolean;
  is_active: boolean;
  created_at?: string;
  // Legacy compat — still returned by backend
  owned_cost_center?: string | null;
}

// ─── API Functions ───────────────────────────────────────────────────────────

export const loginUser = async (payload: LoginPayload): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>("/auth/login", payload);
  return response.data;
};

export const changePassword = async (payload: ChangePasswordPayload): Promise<ChangePasswordResponse> => {
  const response = await api.post<ChangePasswordResponse>("/auth/change-password", payload);
  return response.data;
};

export const getCostCenters = async (): Promise<CostCenter[]> => {
  const response = await api.get<CostCenter[]>("/cost_centers");
  return response.data;
};
